import { h, ComponentChildren, RefObject } from "preact";

import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";
import IconCloseSmall from "../Icons/IconCloseSmall";
import { useDidMountEffect } from "../../hooks";
import Button, { ButtonProps, ButtonVariant } from "../Button";
import NinjaTextButton from "../NinjaTextButton";

import styles from "./Hintbox.module.css";

interface Props {
    class?: string;
    baseRef?: RefObject<HTMLDivElement>;
    style?: CSSProperties;
    image?: ComponentChildren;
    title?: string;
    description: ComponentChildren;
    buttonProps?: HintboxButtonProps;
    secondaryButtonProps?: HintboxButtonProps;
    onDidMount?: () => void;
    onMouseDown?: (event: MouseEvent) => void;
    onClose?: () => void;
}

type HintboxButtonProps = Pick<ButtonProps, "buttonRef" | "variant" | "children" | "isPending" | "onClick">;

function HintboxButton({
    variant,
    ...buttonProps
}: HintboxButtonProps) {
    return variant === ButtonVariant.Ninja
        ? <NinjaTextButton {...buttonProps}/>
        : <Button {...buttonProps} variant={variant}/>;
}

function HintboxPrimaryButton({
    variant = ButtonVariant.Ninja,
    ...buttonProps
}: HintboxButtonProps) {
    return <HintboxButton {...buttonProps} variant={variant}/>;
}

function Hintbox({
    class: cssClass,
    baseRef,
    style,
    image,
    title,
    description,
    buttonProps,
    secondaryButtonProps,
    onDidMount,
    onMouseDown,
    onClose
}: Props) {
    function handleMouseDown(event: MouseEvent) {
        // Hintboxes are usually displayed inside drag&drop and multi-select containers
        // We stop event propagation to make sure that the text inside the hintbox is safely selectable
        event.stopPropagation();
        onMouseDown?.(event);
    }

    if (onDidMount) {
        useDidMountEffect(onDidMount);
    }

    return (
        <div
            class={classname(styles.hintbox, cssClass)}
            ref={baseRef}
            style={style}
            onMouseDown={handleMouseDown}>
            {image}
            <div class={styles.body}>
                {title && <span class={classname(styles.title, "heading-4 medium")}>{title}</span>}
                <span class={classname(styles.description, "body-1")}>{description}</span>
                {(buttonProps || secondaryButtonProps) && (
                    <div class={styles.footer}>
                        {buttonProps && <HintboxPrimaryButton {...buttonProps}/>}
                        {secondaryButtonProps && <HintboxButton {...secondaryButtonProps}/>}
                    </div>
                )}
            </div>
            {onClose && (
                <div
                    class={styles.iconContainer}
                    onClick={onClose}>
                    <IconCloseSmall/>
                </div>
            )}
        </div>
    );
}

export default Hintbox;
export {
    Props as HintboxProps
};
